import {
  ArrowLeftOutlined,
  BankOutlined,
  BellOutlined,
  CarOutlined,
  CheckCircleFilled,
  PlusOutlined,
  ThunderboltFilled,
  WarningFilled,
} from '@ant-design/icons';
import { Button, Progress, Typography, message } from 'antd';
import React, { useMemo, useState } from 'react';
import AddVehicleSheet from './AddVehicleSheet';
import DigiLockerSheet from './DigiLockerSheet';
import InsuranceCompareSheet from './InsuranceCompareSheet';
import InsuranceSection from './InsuranceSection';
import LoanTracker from './LoanTracker';
import NotificationSheet from './NotificationSheet';
import {
  DigiLockerStatus,
  NotificationPrefs,
  Vehicle,
  computeHealthScore,
  daysBetween,
  defaultNotificationPrefs,
  expiryStatus,
  formatDate,
  insurerDeeplinks,
  mockDL,
  mockDigiLocker,
  mockVehicles,
  vehicleAgeMonths,
} from './mockData';
import styles from './styles.module.css';

type Props = {
  onBack: () => void;
};

const titleCase = (s: string) =>
  s
    .toLowerCase()
    .split(' ')
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ');

const Garage: React.FC<Props> = ({ onBack }) => {
  const [vehicles, setVehicles] = useState<Vehicle[]>(mockVehicles);
  const [activeIdx, setActiveIdx] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [dlSheetOpen, setDlSheetOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [insOpen, setInsOpen] = useState(false);
  const [digiLocker, setDigiLocker] = useState<DigiLockerStatus>(mockDigiLocker);
  const [notifPrefs, setNotifPrefs] = useState<NotificationPrefs>(
    defaultNotificationPrefs
  );

  const v = vehicles[activeIdx];
  const score = useMemo(() => (v ? computeHealthScore(v) : 0), [v]);
  const ageMonths = v ? vehicleAgeMonths(v.rc.vehicle_data.manufactured_date) : 0;
  const ageLabel =
    ageMonths < 12
      ? `${ageMonths} mo old`
      : `${(ageMonths / 12).toFixed(1).replace(/\.0$/, '')} yr old`;

  const isCar = v?.rc.vehicle_data.category === 'LMV';

  const handleAdd = (rcNumber: string) => {
    const exists = vehicles.find(
      (vh) => vh.rc.document_id.toLowerCase() === rcNumber.toLowerCase()
    );
    if (exists) {
      message.info('This vehicle is already in your Garage.');
      setAddOpen(false);
      return;
    }
    message.success(
      `Verifying ${rcNumber}… we'll add it once details are fetched.`
    );
    setAddOpen(false);
  };

  const linkInsuranceAA = () => {
    setVehicles((vs) =>
      vs.map((veh, i) =>
        i === activeIdx
          ? {
              ...veh,
              insuranceAA: {
                linked: true,
                premium: 24800,
                idv: 1250000,
                ncb: 25,
                addOns: ['Zero Depreciation', 'Engine Protect'],
                lastClaim: undefined,
                claimSettlementRatio: 88,
              },
            }
          : veh
      )
    );
  };

  const linkLoan = () => {
    setVehicles((vs) =>
      vs.map((veh, i) =>
        i === activeIdx && !veh.loan.linked
          ? {
              ...veh,
              loan: {
                linked: true,
                lender: veh.rc.financier ?? 'HDFC BANK LTD',
                accountNumber: 'XXXXXXX0000',
                sanctionedAmount: 1500000,
                outstandingAmount: 1200000,
                emiAmount: 28000,
                nextEmiDate: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000)
                  .toISOString()
                  .slice(0, 10),
                tenureRemaining: 60,
                interestRate: 9.6,
              },
            }
          : veh
      )
    );
  };

  const openInsurerDeeplink = (company: string) => {
    const url = insurerDeeplinks[company.toUpperCase()] || null;
    if (url) {
      window.open(url, '_blank');
      message.info(`Opening ${titleCase(company)} renewal portal…`);
    } else {
      message.info('Insurer renewal portal not configured');
    }
  };

  if (!v) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className={styles.backBtn} onClick={onBack} aria-label='Back'>
              <ArrowLeftOutlined />
            </button>
            <div>
              <div className={styles.headerTitle}>Your Garage</div>
              <div className={styles.headerSub}>Track every vehicle in one place</div>
            </div>
          </div>
        </div>
        <div className={styles.empty}>
          <CarOutlined style={{ fontSize: 36, color: '#0460ff' }} />
          <Typography.Text style={{ fontWeight: 600 }}>
            No vehicles linked yet
          </Typography.Text>
          <Typography.Text style={{ color: '#6b7280', fontSize: 12 }}>
            Add your RC and we'll auto-track insurance, PUC and registration validity.
          </Typography.Text>
          <Button className={styles.addBtn} onClick={() => setAddOpen(true)}>
            <PlusOutlined /> Add Vehicle
          </Button>
        </div>
        <AddVehicleSheet
          open={addOpen}
          onClose={() => setAddOpen(false)}
          onSubmit={handleAdd}
        />
      </div>
    );
  }

  const docs = [
    {
      key: 'puc',
      name: 'PUC',
      icon: <ThunderboltFilled />,
      iconBg: '#e7f7ee',
      iconFg: '#0a8a3f',
      expiry: v.rc.pucc_data.expiry_date,
      meta: `Cert ${v.rc.pucc_data.pucc_number}`,
      sub: '',
    },
    {
      key: 'rc',
      name: 'RC',
      icon: <CarOutlined />,
      iconBg: '#fff5e0',
      iconFg: '#b96b00',
      expiry: v.rc.expiry_date,
      meta: v.rc.registered_at,
      sub: `Issued ${formatDate(v.rc.issue_date)}`,
    },
    {
      key: 'tax',
      name: 'Road Tax',
      icon: <BankOutlined />,
      iconBg: '#f3e8ff',
      iconFg: '#7c3aed',
      expiry: v.rc.tax_end_date,
      meta: `Valid till ${formatDate(v.rc.tax_end_date)}`,
      sub: '',
    },
  ];

  const nearestExpiry = docs
    .map((d) => ({ ...d, days: daysBetween(d.expiry) }))
    .sort((a, b) => a.days - b.days)[0];

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className={styles.backBtn} onClick={onBack} aria-label='Back'>
            <ArrowLeftOutlined />
          </button>
          <div>
            <div className={styles.headerTitle}>Your Garage</div>
            <div className={styles.headerSub}>
              {vehicles.length} vehicle{vehicles.length > 1 ? 's' : ''} linked
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={styles.backBtn}
            onClick={() => setNotifOpen(true)}
            aria-label='Reminders'
          >
            <BellOutlined />
          </button>
          <Button className={styles.addBtn} onClick={() => setAddOpen(true)}>
            <PlusOutlined /> Add
          </Button>
        </div>
      </div>

      {!digiLocker.connected && (
        <div className={styles.dlBox}>
          <div className={styles.dlIcon}>🔐</div>
          <div className={styles.dlBody}>
            <div className={styles.dlTitle}>Connect DigiLocker</div>
            <div className={styles.dlSub}>
              Pull govt-verified RC, DL, PUC & Insurance into your vault.
            </div>
          </div>
          <button className={styles.dlCta} onClick={() => setDlSheetOpen(true)}>
            Connect
          </button>
        </div>
      )}

      <div className={styles.heroWrap}>
        <div className={`${styles.hero} ${isCar ? styles.heroCar : styles.heroBike}`}>
          <div className={styles.heroTopRow}>
            <div>
              <div className={styles.makerModel}>
                {titleCase(v.rc.vehicle_data.maker_description)}
              </div>
              <div className={styles.makerSub}>
                {v.rc.vehicle_data.maker_model} · {v.rc.vehicle_data.color}
              </div>
            </div>
            <div className={styles.tag}>
              {v.rc.vehicle_data.fuel_type} ·{' '}
              {v.rc.norms_type.replace('BHARAT STAGE ', 'BS')}
            </div>
          </div>

          <div className={styles.plate}>{v.rc.document_id}</div>

          <div className={styles.heroBottomRow}>
            <div className={styles.heroStat}>
              <span className={styles.heroStatLabel}>Owner</span>
              <span className={styles.heroStatValue}>
                {titleCase(v.rc.owner_data.name).split(' ').slice(0, 2).join(' ')}
              </span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatLabel}>Age</span>
              <span className={styles.heroStatValue}>{ageLabel}</span>
            </div>
            <div className={styles.heroStat}>
              <span className={styles.heroStatLabel}>Registered</span>
              <span className={styles.heroStatValue}>
                {v.rc.registered_at.split(',')[0]}
              </span>
            </div>
          </div>
        </div>

        {vehicles.length > 1 && (
          <div className={styles.dotRow}>
            {vehicles.map((_, i) => (
              <span
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`${styles.dot} ${i === activeIdx ? styles.dotActive : ''}`}
                style={{ cursor: 'pointer' }}
              />
            ))}
          </div>
        )}
      </div>

      <div className={styles.layout}>
        <div className={styles.left}>
          <div className={styles.healthCard}>
            <div className={styles.scoreRing}>
              <Progress
                type='circle'
                percent={score}
                size={84}
                strokeColor={
                  score >= 80 ? '#0a8a3f' : score >= 60 ? '#b96b00' : '#c81e3a'
                }
                strokeWidth={10}
                format={(p) => (
                  <span style={{ fontSize: 20, fontWeight: 700 }}>{p}</span>
                )}
              />
            </div>
            <div className={styles.healthBody}>
              <div className={styles.healthTitle}>Vehicle Health</div>
              <div className={styles.healthLine}>
                {nearestExpiry.days < 0 ? (
                  <>
                    <WarningFilled style={{ color: '#c81e3a' }} />
                    {nearestExpiry.name} expired{' '}
                    {Math.abs(nearestExpiry.days)} days ago
                  </>
                ) : nearestExpiry.days <= 30 ? (
                  <>
                    <WarningFilled style={{ color: '#c81e3a' }} />
                    {nearestExpiry.name} expires in {nearestExpiry.days} days
                  </>
                ) : (
                  <>
                    <CheckCircleFilled style={{ color: '#0a8a3f' }} />
                    All docs valid · next renewal in {nearestExpiry.days} days
                  </>
                )}
              </div>
              <div className={styles.healthLine} style={{ marginTop: 2 }}>
                Status: <strong style={{ color: '#0a8a3f' }}>{v.rc.status}</strong>
                {v.rc.is_commercial ? ' · Commercial' : ' · Personal'}
              </div>
            </div>
          </div>

          <div className={styles.sectionTitle}>Insurance</div>
          <InsuranceSection
            rc={v.rc}
            insuranceAA={v.insuranceAA}
            onLinkAA={linkInsuranceAA}
            onBuy={() => setInsOpen(true)}
            onRenew={() => openInsurerDeeplink(v.rc.insurance_data.company)}
          />

          <div className={styles.sectionTitle}>Documents</div>
          <div className={styles.docGrid}>
            {docs.map((d) => {
              const days = daysBetween(d.expiry);
              const status = expiryStatus(days);
              return (
                <div key={d.key} className={styles.docCard}>
                  <div className={styles.docHead}>
                    <div className={styles.docName}>
                      <span
                        className={styles.docIcon}
                        style={{ background: d.iconBg, color: d.iconFg }}
                      >
                        {d.icon}
                      </span>
                      {d.name}
                    </div>
                    <span
                      className={`${styles.statusPill} ${
                        status.tone === 'good'
                          ? styles.toneGood
                          : status.tone === 'warn'
                          ? styles.toneWarn
                          : styles.toneBad
                      }`}
                    >
                      {status.label}
                    </span>
                  </div>
                  <div>
                    <div className={styles.docDays}>
                      {days < 0 ? `−${Math.abs(days)}` : days}
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          marginLeft: 6,
                          color: '#6b7280',
                        }}
                      >
                        days
                      </span>
                    </div>
                    <div className={styles.docDaysSub}>
                      {days < 0 ? 'past expiry' : 'until renewal'}
                    </div>
                  </div>
                  <div className={styles.docExpiry}>
                    Expires {formatDate(d.expiry)} · {d.meta}
                  </div>
                  {d.sub && (
                    <div
                      className={styles.docExpiry}
                      style={{ marginTop: -6, fontSize: 10 }}
                    >
                      {d.sub}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className={styles.sectionTitle}>Specs</div>
          <div className={styles.specTileRow}>
            <div className={styles.specTile}>
              <div className={styles.specTileValue}>
                {v.rc.vehicle_data.cubic_capacity}
              </div>
              <div className={styles.specTileLabel}>cc engine</div>
            </div>
            <div className={styles.specTile}>
              <div className={styles.specTileValue}>
                {v.rc.vehicle_data.seating_capacity}
              </div>
              <div className={styles.specTileLabel}>seats</div>
            </div>
            <div className={styles.specTile}>
              <div
                className={styles.specTileValue}
                style={{ fontSize: 14 }}
              >
                {v.rc.vehicle_data.body_type}
              </div>
              <div className={styles.specTileLabel}>body</div>
            </div>
            <div className={styles.specTile}>
              <div className={styles.specTileValue}>
                {v.rc.vehicle_data.number_of_cylinders}
              </div>
              <div className={styles.specTileLabel}>
                cyl{v.rc.vehicle_data.number_of_cylinders !== '1' ? 's' : ''}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.right}>
          {/* DL card — only show on first vehicle to avoid repetition */}
          {activeIdx === 0 && digiLocker.connected && (
            <div className={styles.dlCard}>
              <div className={styles.dlCardTitle}>Driving License</div>
              <div className={styles.dlCardNumber}>{mockDL.dl_number}</div>
              <div style={{ fontSize: 12, fontWeight: 600 }}>
                {titleCase(mockDL.holder_name)}
              </div>
              <div className={styles.dlCardRow}>
                <span>Valid till {formatDate(mockDL.expiry_date)}</span>
                <span>Classes: {mockDL.vehicle_classes.join(', ')}</span>
              </div>
            </div>
          )}

          <div className={styles.sectionTitle} style={{ margin: '4px 0 0' }}>
            Vehicle loan
          </div>
          <LoanTracker
            loan={v.loan}
            financier={v.rc.financier}
            onLinked={linkLoan}
          />

          <div className={styles.specCard}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              Reminders & Auto-pay
            </div>
            <Typography.Text style={{ fontSize: 12, color: '#6b7280' }}>
              Never miss a renewal. We'll ping you 30/15/7/1 day before each
              expiry on your chosen channels.
            </Typography.Text>
            <Button
              type='link'
              style={{ paddingLeft: 0, fontWeight: 600 }}
              onClick={() => setNotifOpen(true)}
            >
              Customise reminders →
            </Button>
          </div>
        </div>
      </div>

      <AddVehicleSheet
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAdd}
      />
      <DigiLockerSheet
        open={dlSheetOpen}
        onClose={() => setDlSheetOpen(false)}
        onConnected={(docs) => {
          setDigiLocker({
            connected: true,
            connectedAt: new Date().toISOString(),
            documents: docs,
          });
          setDlSheetOpen(false);
          message.success('DigiLocker connected. 4 documents synced.');
        }}
      />
      <NotificationSheet
        open={notifOpen}
        prefs={notifPrefs}
        onClose={() => setNotifOpen(false)}
        onSave={(p) => {
          setNotifPrefs(p);
          setNotifOpen(false);
          message.success('Reminder preferences saved');
        }}
      />
      <InsuranceCompareSheet
        open={insOpen}
        vehicleLabel={`${titleCase(v.rc.vehicle_data.maker_description)} ${
          v.rc.vehicle_data.maker_model
        }`}
        onClose={() => setInsOpen(false)}
      />
    </div>
  );
};

export default Garage;
